import {createSlice, PayloadAction, createSelector} from '@reduxjs/toolkit';
import type {RootState} from '../../types';
import type {IPageState, IPageUrls, ILink, ISeoMetadata, IJsonLd, ReceiveRoutePayload} from './types';

const pageSelector = (state: RootState) => state.page;
const seoSelector = (state: RootState) => state.page.seo;

export const pageSlice = createSlice({
	name: 'page',
	initialState: {
		route: {
			currentRoute: null,
			params: {},
			isHomePage: false,
		},
		seo: {
			metadata: {},
			pageUrls: {},
			links: [],
			jsonLd: [],
			canonical: '',
		},
		transparentHeader: false,
		baseNavigationColor: false,
		highContrastHeader: false,
		hideFooter: false,
	},
	reducers: {
		receiveMetadata: (state: IPageState, action: PayloadAction<ISeoMetadata>) => {
			state.seo.metadata = action.payload;
		},
		receivePageUrls: (state: IPageState, action: PayloadAction<IPageUrls>) => {
			state.seo.pageUrls = action.payload;
		},
		receiveLinks: (state: IPageState, action: PayloadAction<ILink[]>) => {
			state.seo.links = action.payload;
		},
		receiveJsonLd: (state: IPageState, action: PayloadAction<IJsonLd[]>) => {
			state.seo.jsonLd = action.payload;
		},
		receiveRoute: (state: IPageState, action: PayloadAction<ReceiveRoutePayload>) => {
			const newRouteState = {...state.route, ...action.payload};

			// Remove params key if exists and payload doesn't have params key
			if (!action.payload.params && state.route.params) {
				delete newRouteState.params;
			}

			state.route = newRouteState;
		},
		receiveCanonical: (state: IPageState, action: PayloadAction<string>) => {
			state.seo.canonical = action.payload;
		},
	},
});

export const {receiveMetadata, receivePageUrls, receiveLinks, receiveJsonLd, receiveRoute, receiveCanonical} =
	pageSlice.actions;

export const selectPage = createSelector(pageSelector, (page) => page);
export const selectSeo = createSelector(pageSelector, ({seo}) => seo);
export const selectRoute = createSelector(pageSelector, ({route}) => route);
export const selectMetadata = createSelector(seoSelector, ({metadata}) => metadata);
export const selectLinks = createSelector(seoSelector, ({links}) => links);
export const selectPageUrls = createSelector(seoSelector, ({pageUrls}) => pageUrls);
export const selectJsonLd = createSelector(seoSelector, ({jsonLd}) => jsonLd);
export const selectCanonical = createSelector(seoSelector, ({canonical}) => canonical);
export default pageSlice.reducer;
